import i18n from 'live/services/i18n'
import React, { useEffect, useState, ReactElement, useCallback } from 'react'
import { ProviderImport } from '../providers/ProviderImport'
import { ConstantsImport, importErrors } from '../constants/ConstantsImport'
import ImportUtil from '../util/ImportUtil'
import { SortCompareName } from '../util/SortsUtil'
import NoResults from './NoResults'
import * as Style from '../style/style'
import { webConfig } from '../types/webConfig'
import { canManageWells } from '../../../utils/permission'

const providerImport = new ProviderImport()

export default function FileImport(props: {
    onSelectFile: Function
    iconName?: string
    fileType: string
    filterBy?: string
    setEnable: Function
}): ReactElement {
    const [loaded, setLoaded] = useState(false)
    const [error, setError] = useState({} as any)
    const [listWebConfig, setListWebConfig] = useState([] as any)
    const [list, setList] = useState([])
    const [listCache, setListCache] = useState([])
    const [searchTxt, setSearchTxt] = useState('')
    const [selectedFile, setSelectedFile] = useState({})
    const [valid, setValid] = useState(false)
    const [page, setPage] = useState(0)
    const isAttachment = ConstantsImport.attachmentTypes.includes(props.fileType)

    const handleError = useCallback((error: any) => {
        console.error(error)
        if (error.response?.status === 500 && error.response?.data?.message.indexOf('local connections') > -1) {
            error.msg = ConstantsImport.msg.onlyLocal
        } else if (error.httpStatus === 404) {
            error.msg = ConstantsImport.msg.notFound
        } else {
            error.msg = ConstantsImport.msg.notConnect
        }

        loadedData(true, error)
    }, [])

    const getTreeByFileType = useCallback(() => {
        loadedData(false)
        providerImport
            .getByFileType(props.fileType)
            .then((result: any) => {
                const listResult = isAttachment ? result : result.files
                setList(listResult.sort(SortCompareName))
                setListCache(listResult)
                loadedData(true)
                setPage(2)
            })
            .catch(handleError)
    }, [handleError, props.fileType, providerImport])

    useEffect(() => {
        if (canManageWells()) {
            providerImport
                .getWebExtensionInstances()
                .then((webConfigs: Array<webConfig>) => {
                    setLoaded(true)
                    if (!webConfigs || (webConfigs && webConfigs.length == 0)) {
                        setError(importErrors.needregister)
                    } else if (webConfigs && webConfigs.length === 1) {
                        providerImport.setRequestOptions(webConfigs[0])
                        setPage(1)
                        getTreeByFileType()
                    } else {
                        setPage(1)
                        setListWebConfig(webConfigs)
                        loadedData(true)
                        setListCache(webConfigs as any)
                    }
                })
                .catch(handleError)
        }
    }, [getTreeByFileType, handleError, providerImport])

    const loadedData = (state: boolean, error?: any): void => {
        setError(error)
        setLoaded(state)
    }

    const getByUrl = (url: string): void => {
        loadedData(false)
        providerImport
            .getByUrl(url)
            .then((result: any) => props.onSelectFile(result.revision?.content))
            .catch(handleError)
    }

    const getAttachment = (fileId: string, fileRevision: string): void => {
        loadedData(false)
        providerImport
            .getAttachment(fileId, fileRevision)
            .then((attachment: string) => {
                props.onSelectFile(attachment)
            })
            .catch(handleError)
    }

    const selectItemList = (element: any, index: number): void => {
        const url = element.url
        const idRev = ImportUtil.getFileIdRevision(url)
        const fileId = idRev.fileId
        const fileRevision = idRev.fileRevision

        if (isAttachment) getAttachment(fileId, fileRevision)
        else getByUrl(element.url)
    }

    const onChangeSearch = (event: any): void => {
        const searchTxt = event.target.value
        filterLists(searchTxt)
        if (event.keyCode == 13) setSelectedFile(list[0])
        if ((event.keyCode === 10 || event.keyCode === 13) && event.ctrlKey) {
            // ctrl+enter
            onSubmit()
        }
    }

    const filterLists = (searchTxt: string): void => {
        setSearchTxt(searchTxt)
        if (page === 1) {
            if (!searchTxt) setListWebConfig(listCache)
            else setListWebConfig(ImportUtil.filterBy(listCache, searchTxt, 'qualifier'))
        } else {
            if (!searchTxt) setList(listCache)
            else setList(ImportUtil.filterBy(listCache, searchTxt, props.filterBy || 'name'))
        }
    }

    const selectWebConfig = (webConfig: any): void => {
        providerImport.setRequestOptions(webConfig)
        getTreeByFileType()
    }

    const onSubmit = (): void => {
        if (!valid) return
        setValid(true)
        props.onSelectFile(selectedFile)
    }

    return (
        <Style.Content>
            {!error && (
                <Style.ContainerInput>
                    <i className="fas fa-search" />
                    <input
                        className="searchTxt"
                        type="text"
                        disabled={!loaded}
                        style={{ background: 'white', cursor: loaded ? 'pointer' : 'not-allowed' }}
                        placeholder={i18n('Search...')}
                        value={searchTxt}
                        onChange={onChangeSearch}
                        ref={input => input && input.focus()}
                    />
                    {searchTxt && <i className="fas fa-times" onClick={() => filterLists('')} />}
                </Style.ContainerInput>
            )}{' '}
            {!error && loaded && page > 2 && (
                <Style.ContainerSubtitle>
                    <Style.ContainerBack
                        onClick={() => {
                            console.log('back!')
                        }}
                    >
                        <i className="fas fa-arrow-left" />
                        <span>{i18n('Back')}</span>
                    </Style.ContainerBack>
                </Style.ContainerSubtitle>
            )}
            {!loaded && <Style.Loading className="loading" />}
            {!error &&
                page === 1 &&
                listWebConfig?.map((webConfig: webConfig, key: number) => (
                    <Style.ListItemContainer
                        key={key}
                        className="listItem borderItem"
                        onClick={() => selectWebConfig(webConfig)}
                    >
                        <i className="fal fa-sitemap" style={{ marginRight: '10px' }}></i>
                        <span>{webConfig.qualifier || webConfig.uri || 'liverig-pocoweb-wellimport'} </span>
                    </Style.ListItemContainer>
                ))}
            {!error &&
                list?.map((element: any, index: number) => (
                    <Style.ListItemContainer
                        key={index}
                        onClick={() => selectItemList(element, index)}
                        className="borderItem"
                    >
                        <div>
                            {!isAttachment && props.iconName && (
                                <Style.IconFile className={`fal fa-${props.iconName}`}></Style.IconFile>
                            )}
                            {element.file_type && isAttachment && (
                                <i className={element.file_type && element.file_type.replace('.', '') + ' iconFile'} />
                            )}
                            {element.name && <span> ({element.name}) </span>}
                            {element.created_at && (
                                <span className="createdAt right">{ImportUtil.getDate(element.created_at)}</span>
                            )}
                        </div>
                    </Style.ListItemContainer>
                ))}
            {error && (
                <Style.ErrorStyled>
                    {i18n(error?.msg || (loaded && ConstantsImport.msg.genericError))}
                </Style.ErrorStyled>
            )}
            {(loaded &&
                ((!error && list?.length === 0 && page === 2) ||
                    (listWebConfig.length === 0 && page === 1) ||
                    !list)) ||
                (error && (
                    <NoResults
                        onClearResults={() => filterLists('')}
                        msg={ConstantsImport.msg.noResults}
                        txtClear={i18n('Clear')}
                    />
                ))}
        </Style.Content>
    )
}
